import { Component, signal, computed, inject, effect, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from "@angular/forms";
import { MsalService } from "@azure/msal-angular";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";

import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { SelectModule } from "primeng/select";
import { DividerModule } from "primeng/divider";
import { AvatarModule } from "primeng/avatar";

import { UserActions } from "src/app/store/user/user.actions";
import { NavigationActions } from "src/app/store/navigation/navigation.actions";
import { selectUserContext } from "src/app/store/user/user.selectors";
import { selectAvailableLangs } from "src/app/store/application/settings/settings.selectors";
import { environment } from "src/environments/environment";
import { selectCurrencies } from "src/app/store/application/data/currency/currencies/currencies.selectors";
import { phoneNumberValidator } from "src/app/shared/validators/validations";
import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { getLanguageFlagUrl } from "src/app/shared/constants/flags.constants";
import { NavRoutesService } from "src/app/navigation/nav-routes.service";

@Component({
    selector: "app-settings-drawer",
    templateUrl: "./settings-drawer.component.html",
    styleUrls: ["./settings-drawer.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        InputTextModule,
        ButtonModule,
        SelectModule,
        DividerModule,
        AvatarModule,
        CoreErrorsComponent,
    ],
})
export class SettingsDrawerComponent {
    // === Dependencies ===
    public readonly router = inject(Router);
    private readonly authService = inject(MsalService);
    private readonly store = inject(Store);
    private readonly navRoutes = inject(NavRoutesService);

    // === Constants ===
    public readonly environment = environment;

    // === Store Selectors ===
    public readonly user = toSignal(this.store.select(selectUserContext), { initialValue: null });
    public readonly availableLanguages = toSignal(this.store.select(selectAvailableLangs), { initialValue: [] });
    public readonly currencies = toSignal(this.store.select(selectCurrencies), { initialValue: [] });

    // === Local State ===
    public readonly editingPhoneNumber = signal(false);

    // === Form ===
    public readonly form = new FormGroup({
        phoneNumber: new FormControl({ value: null, disabled: true }, [phoneNumberValidator()]),
        company: new FormControl(null, [Validators.required]),
        userLanguage: new FormControl(null, [Validators.required]),
    });

    public readonly companyOptions = computed(() => {
        const u = this.user();
        if (!u?.companies) return [];
        return u.companies.map(company => ({
            label: company.name.toUpperCase(),
            value: company.id,
            countryCode: company.countryCode,
        }));
    });

    public readonly languageOptions = computed(() => {
        return this.availableLanguages().map(lang => ({
            label: lang.toUpperCase(),
            value: lang,
        }));
    });

    public readonly initials = computed(() => {
        const u = this.user();
        if (!u?.name) return "?";
        const names = u.name.split(" ");
        if (names.length >= 2) {
            return (names[0][0] + names[names.length - 1][0]).toUpperCase();
        }
        return names[0][0]?.toUpperCase() || "?";
    });

    public readonly userRoles = computed(() => {
        const roles = this.user()?.userRoles;
        if (!roles) return [];
        const roleKeys: string[] = [];
        if (roles.standard) roleKeys.push("USER.ROLES.STANDARD");
        if (roles.centralSalesSupport) roleKeys.push("USER.ROLES.CENTRAL_SALES_SUPPORT");
        if (roles.superUser) roleKeys.push("USER.ROLES.SUPER_USER");
        if (roles.contractAdmin) roleKeys.push("USER.ROLES.CONTRACT_ADMIN");
        if (roles.developer) roleKeys.push("USER.ROLES.DEVELOPER");
        return roleKeys;
    });

    // === Constructor with Effects ===
    constructor() {
        // Patch form when user data changes
        effect(() => {
            const u = this.user();
            if (u) {
                this.form.patchValue({
                    phoneNumber: u.phoneNumber,
                    company: u.selectedCompany?.id,
                    userLanguage: u.siteLanguageCode,
                });
            }
        });
    }

    // === Public Methods ===
    public editPhoneNumber(): void {
        this.editingPhoneNumber.set(true);
        this.form.get("phoneNumber")?.enable();
    }

    public savePhoneNumber(): void {
        if (this.form.get("phoneNumber")?.invalid) {
            return;
        }
        this.editingPhoneNumber.set(false);
        const newPhone = this.form.value.phoneNumber;
        this.store.dispatch(UserActions.updatePhoneNumber({ phoneNumber: newPhone }));
        this.form.get("phoneNumber")?.disable();
    }

    public changeCompany(event: any): void {
        const companyId = event.value;
        const u = this.user();
        const newCompany = u?.companies.find(c => c.id === companyId);
        if (newCompany) {
            this.store.dispatch(UserActions.changeCompany({ company: newCompany }));
        }
    }

    public changeUserLanguage(event: any): void {
        const selectedLanguage = event.value;
        if (selectedLanguage) {
            this.store.dispatch(UserActions.updateUserLanguage({ language: selectedLanguage }));
        }
    }

    public openKeyboardShortcuts(): void {
        if (this.navRoutes.companyId()) {
            this.router.navigate(this.navRoutes.route("home", "shortcuts"));
        }
        this.store.dispatch(NavigationActions.toggleSettingsDrawer());
    }

    public logout(): void {
        this.authService.logoutRedirect();
    }

    public getLanguageFlagUrl(langCode: string): string {
        return getLanguageFlagUrl(langCode);
    }
}
